<template>
  <div class="dialog-container" v-loading="isLoading">
    <el-alert class="mb-3" :title="errorMessage" type="error" show-icon v-if="errorMessage"></el-alert>
    <el-row :gutter="18">
      <el-col :span="24">
        <el-form ref="saveClient" :model="form" :rules="rules" label-width="175px">
          <el-form-item label="First Name" prop="firstName">
            <el-input v-model="form.firstName" placeholder="First Name"></el-input>
          </el-form-item>
          <el-form-item label="Last Name" prop="lastName">
            <el-input v-model="form.lastName" placeholder="Last Name"></el-input>
          </el-form-item>
          <el-form-item label="Cell Phone" prop="cellPhone">
            <el-input v-model="form.cellPhone" v-mask="'(###) ###-####'" placeholder="Cell Phone"></el-input>
          </el-form-item>
          <el-form-item label="Email Address" prop="emailAddress">
            <el-input v-model="form.emailAddress" placeholder="Email Address"></el-input>
          </el-form-item>
          <el-form-item label="Office" prop="selectedOfficeId">
            <el-select v-model="form.selectedOfficeId" value-key="id"
              :disabled="!isDistrictManager || allUserOffices.length === 1">
              <el-option v-for="item in formattedUserOffices" :key="item.id" :label="item.name" :value="item.id">
              </el-option>
            </el-select>
          </el-form-item>
        </el-form>
      </el-col>
    </el-row>
    <el-row :gutter="18" class="dialog-footer">
      <el-col :span="24" class="align-right">
        <el-button @click="closeDialog">Cancel</el-button>
        <el-button type="primary" @click="saveClient">Save Client</el-button>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import get from 'lodash/get';
import map from 'lodash/map';
import { Required } from '@/util/formValidators';
import api from '@/api';
import DocutaxClient from '@/model/DocutaxClient';
import OfficeToRefType from '@/util/OfficeToRefType';

export default {
  name: 'EditDocutaxClientDialog',
  props: {
    client: {
      type: DocutaxClient,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      form: {
        firstName: '',
        lastName: '',
        cellPhone: '',
        emailAddress: '',
        selectedOfficeId: null,
      },
      rules: {
        firstName: [Required],
        lastName: [Required],
        cellPhone: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error('Cell phone number is required.'));
              } else if (!value.match(/^\([0-9]{3}\)\s[0-9]{3}-[0-9]{4}$/i)) {
                callback(new Error('Please enter a valid phone number in (999) 999-9999 format'));
              } else {
                callback();
              }
            },
            trigger: 'blur',
          },
        ],
        emailAddress: [
          {
            required: false,
            validator: (rule, value, callback) => {
              if (!value) {
                callback();
              } else if (!value.match(/^.+@.+\..+$/i)) {
                callback(new Error('Please enter a valid email address'));
              } else {
                callback();
              }
            },
            trigger: 'blur',
          },
        ],
        selectedOfficeId: [Required],
      },
    };
  },
  methods: {
    async saveClient() {
      this.errorMessage = null;
      this.$refs.saveClient.validate(async (valid) => {
        if (valid) {
          this.isLoading = true;
          try {
            const response = await api.docutaxClient.update(this.client.id, this.form);

            this.$notify.success({
              title: 'Success',
              message: 'Client saved.',
              position: 'bottom-right',
            });

            this.$emit('clientUpdated', new DocutaxClient(response.data));
            this.closeDialog();
          } catch (e) {
            this.errorMessage = get(e, 'response.data', e.message || 'An error occurred.');
          } finally {
            this.isLoading = false;
          }

          return true;
        }
        return false;
      });
    },
    closeDialog() {
      this.$emit('close');
    },
    initForm(client) {
      if (client) {
        this.$nextTick(() => {
          this.form.firstName = client.firstName || '';
          this.form.lastName = client.lastName || '';
          this.form.cellPhone = client.cellPhone || '';
          this.form.emailAddress = client.emailAddress || '';
          this.form.selectedOfficeId = client.selectedOfficeId || null;
        });
      }
    },
  },
  watch: {
    client(val) {
      this.initForm(val);
    },
  },
  computed: {
    ...mapGetters('User', [
      'allUserOffices',
      'isDistrictManager',
    ]),
    formattedUserOffices() {
      return map(this.allUserOffices, office => OfficeToRefType(office));
    },
  },
  mounted() {
    if (this.client) {
      this.initForm(this.client);
    }
  },
};
</script>
